import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import ExtractionDataIcon from '~/common/componentMapper/QuestionExtraction/components/ExtractionDataIconComponent';
import Flex from '~/common/components/Flex';
import { Tbody, Th, Tr } from '~/common/components/Tables';

import SnapShotButton from '../../components/smartQuestionComponents/SnapshotButton';
import QuestionResolutionWrapper from '../../components/utilityComponents/QuestionResolutionWrapper';
import InputController from '../../controllers/InputController';
import {
  useQuestionLibraryActions,
  useQuestionLibraryStore,
} from '../../store/questionLibraryStore';
import { SMART_ANSWER_LABEL_TYPES } from '../../utils/constants';
import { useConditionsEvaluator } from '../../utils/useConditionsEvaluator';
import {
  StyledTHead,
  StyledTable,
  StyledTd,
} from './../../components/styledComponents/styledTableComponents';
import { useSelectSmartAnswer } from './../../utils/hooks';

type ComparisonTableProps = {
  question?: Record<string, any>;
  isParentShowing?: boolean;
};

/**
 * @typedef {object} ComparisonTableProps
 * @property {import('./../../types/complexQuestion').ComparisonTableQuestionProps} question
 *
 * @param {ComparisonTableProps} props
 * @returns {JSX.Element}
 */
export default function ComparisonTable({
  question,
  isParentShowing = true,
}: Readonly<ComparisonTableProps>) {
  const {
    columns,
    name,
    rowDataKey,
    rowDataSource,
    questions,
    resolutions,
    conditions = {},
    hasBottomBorder = true,
  } = question;

  const { watch } = useFormContext();
  const { isVisible } = useConditionsEvaluator(conditions);
  const allFields = watch();
  const [screenDataRowData, isAiHiddenByQuestion] = useQuestionLibraryStore(
    useShallow((s) => [
      s.screenData[rowDataKey],
      s.screenData.isAiHiddenByQuestion ?? {},
    ]),
  );

  const tableRows = useMemo(() => {
    if (rowDataSource === 'screenData') {
      return screenDataRowData ?? [];
    }

    return allFields[rowDataKey] ?? [];
  }, [rowDataKey, rowDataSource, screenDataRowData, allFields]);

  const normalizeRowKey = useCallback((value) => {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }, []);

  const rowQuestionNameMap = useMemo(() => {
    const map = new Map();

    (question.questionMatches ?? []).forEach((match) => {
      const normalizedKey = normalizeRowKey(match.rowKey);

      if (normalizedKey && match.questionName) {
        map.set(normalizedKey, match.questionName);
      }
    });

    return map;
  }, [question.questionMatches, normalizeRowKey]);

  const questionsByName = useMemo(() => {
    return (questions ?? []).reduce((acc, currentQuestion) => {
      if (currentQuestion?.name) {
        acc[currentQuestion.name] = currentQuestion;
      }

      return acc;
    }, {});
  }, [questions]);

  const visibleRows = useMemo(() => {
    return tableRows.reduce((acc, row, index) => {
      const matchedQuestionName = rowQuestionNameMap.get(
        normalizeRowKey(row?.key),
      );

      const resolvedQuestion = matchedQuestionName
        ? questionsByName[matchedQuestionName]
        : questions[index];

      const isRowAiHidden = Boolean(
        resolvedQuestion?.name && isAiHiddenByQuestion?.[resolvedQuestion.name],
      );

      if (!isRowAiHidden) {
        acc.push({ row, resolvedQuestion, index });
      }

      return acc;
    }, []);
  }, [
    tableRows,
    rowQuestionNameMap,
    normalizeRowKey,
    questionsByName,
    questions,
    isAiHiddenByQuestion,
  ]);

  const isTableVisible = isVisible && visibleRows.length > 0;

  return (
    <QuestionResolutionWrapper
      resolutions={resolutions}
      hasBottomBorder={hasBottomBorder}
      isVisible={isTableVisible}
    >
      <StyledTable id={name}>
        <StyledTHead>
          <Tr>
            {columns.map((column) => (
              <Th key={column.header}>{column.header}</Th>
            ))}
          </Tr>
        </StyledTHead>
        <Tbody>
          {visibleRows.map(({ row, resolvedQuestion, index }) => (
            <Tr key={resolvedQuestion?.name ?? row?.key ?? index}>
              {columns.map((column) => (
                <ComparisonTableTd
                  key={`${column.header}-${resolvedQuestion?.name ?? index}`}
                  column={column}
                  nonQuestionTypeFallback={row[column.field]}
                  question={resolvedQuestion}
                  totalColumns={columns.length}
                  isParentShowing={isParentShowing}
                />
              ))}
            </Tr>
          ))}
        </Tbody>
      </StyledTable>
    </QuestionResolutionWrapper>
  );
}

type ComparisonTableTdProps = {
  totalColumns?: any;
  column?: any;
  question?: any;
  nonQuestionTypeFallback?: any;
  isParentShowing?: boolean;
};

function ComparisonTableTd({
  column,
  totalColumns,
  question,
  nonQuestionTypeFallback,
  isParentShowing,
}: Readonly<ComparisonTableTdProps>) {
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const smartAnswer = useSelectSmartAnswer(question.name);

  const handleFocus = useCallback(
    (questionName) => {
      setFocusedQuestion(questionName, smartAnswer);
    },
    [setFocusedQuestion, smartAnswer],
  );

  return (
    <StyledTd width={`${100 / totalColumns}%`}>
      {column.type === 'question' ? (
        <Flex align={'center'} gap={'0.325rem'} padding={'0.5rem 0.25rem'}>
          <InputController
            question={question}
            isRequirable={true}
            onFocus={() => handleFocus(question.name)}
            smartAnswerLayout={
              SMART_ANSWER_LABEL_TYPES.NO_SNAPSHOT_BUTTON_OR_ICON
            }
            isParentShowing={isParentShowing}
          />
          <ExtractionDataIcon
            iconType={smartAnswer?.iconType}
            testId={`${question.name}-extraction-icon-${smartAnswer?.iconType}`}
          />
          <SnapShotButton
            snippets={smartAnswer?.aiResponse?.snippets}
            questionName={question.name}
          />
        </Flex>
      ) : (
        nonQuestionTypeFallback
      )}
    </StyledTd>
  );
}
